/**
 * StateInset Component
 * 
 * Displays small northeastern states as clickable boxes
 * for better visibility and interaction
 */

import React from 'react';
import { STATE_CONFIG } from '../data/stateConfig.js';
import {
  getStateStatus,
  getStateColor,
  getTooltipMessage,
  getNavigationURL,
  isStateClickable
} from '../utils/stateClassifier.js';
import '../styles/inset.css';

export function StateInset({ onStateHover, onStateLeave, onStateClick, hoveredState }) {
  // Small northeastern states that are hard to click on the main map
  const insetStates = ['VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC'];

  const handleClick = (stateCode) => {
    if (onStateClick) {
      onStateClick(stateCode);
    }
  };

  const handleMouseEnter = (stateCode, event) => {
    if (onStateHover) {
      onStateHover(stateCode, event);
    }
  };

  const handleMouseLeave = () => {
    if (onStateLeave) {
      onStateLeave();
    }
  };

  const handleKeyPress = (event, stateCode) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(stateCode);
    }
  };

  // Render individual state box
  const renderState = (stateCode) => {
    const state = STATE_CONFIG[stateCode];
    if (!state) return null;

    const status = getStateStatus(stateCode);
    const isHovered = hoveredState === stateCode;
    const color = getStateColor(status, isHovered);
    const clickable = isStateClickable(stateCode);
    const tooltipMsg = getTooltipMessage(stateCode);

    return (
      <div
        key={stateCode}
        className={`inset-state ${clickable ? 'clickable' : ''} ${isHovered ? 'hovered' : ''}`}
        style={{
          backgroundColor: color,
          cursor: clickable ? 'pointer' : 'default'
        }}
        onMouseEnter={(e) => handleMouseEnter(stateCode, e)}
        onMouseLeave={handleMouseLeave}
        onClick={() => handleClick(stateCode)}
        onKeyPress={(e) => handleKeyPress(e, stateCode)}
        role={clickable ? 'link' : 'button'}
        tabIndex={0}
        aria-label={`${state.name} - ${tooltipMsg}`}
        title={tooltipMsg}
      >
        <span className="inset-state-code">{stateCode}</span>
      </div>
    );
  };

  return (
    <div className="state-inset" role="region" aria-label="Northeastern states detail view">
      {/* VT and NH - positioned above NY, left of Maine */}
      <div className="inset-group inset-north">
        {renderState('VT')}
        {renderState('NH')}
      </div>

      {/* Left column: MA, CT, DE, DC */}
      <div className="inset-group inset-left-column">
        {renderState('MA')}
        {renderState('CT')}
        {renderState('DE')}
        {renderState('DC')}
      </div>

      {/* Right column: RI, NJ, MD - aligned to right edge */}
      <div className="inset-group inset-right-column">
        {renderState('RI')}
        {renderState('NJ')}
        {renderState('MD')}
      </div>
    </div>
  );
}
